import { compact, first } from 'lodash';

import {
  ClientProfileFormShape,
  ClientProfileRelationshipFormShape,
} from '@/modules/clientProfiles/ClientProfile.types';
import { getTypeOrUndefined } from '@/modules/entities/EntitySubforms/utils/shared/common.utils';
import {
  ClientProfileDisplayNamePattern,
  ClientProfileRelationshipType,
  CreateClientProfileWithDetailsInput,
  CreateNewClientProfileRelationshipInput,
  UpdateClientProfileRelationship,
} from '@/types/schema';
import { getValueOrClear } from '@/utils/graphqlFormUtils';
import { getNodes } from '@/utils/graphqlUtils';

import { ClientProfileSideDrawerContextShape } from './ClientProfileModal.context';
import { BeneficiaryFormDataQuery } from './graphql/ClientProfileFormData.generated';
import { UpdateClientProfileModalMutationVariables } from './graphql/UpdateClientProfileModal.generated';

function getValueOrClearTrimmed<F extends string>(
  key: F,
  value: string | null | undefined
) {
  const trimmed = value?.trim();
  return getValueOrClear(key, trimmed || undefined);
}

export function mapDataToForm({
  data,
  clientProfileId,
  initialRelationships,
}: {
  data: BeneficiaryFormDataQuery;
  clientProfileId: string | null;
  initialRelationships?: Record<string, ClientProfileRelationshipType>;
}): ClientProfileFormShape {
  const household = first(getNodes(data?.households));

  const clientProfile =
    household?.clientProfiles?.find(
      (profile) => profile.id === clientProfileId
    ) ?? null;

  // Build relationships array starting with primary clients
  const relationships: ClientProfileRelationshipFormShape[] =
    household?.possiblePrimaryClients
      .filter((primaryClient) => primaryClient.id !== clientProfileId)
      .map((primaryClient) => {
        const existingRelationship = clientProfile?.relationships?.find(
          (relationship) => relationship.toClientProfile.id === primaryClient.id
        );
        const initialRelationshipType =
          initialRelationships?.[primaryClient.id];
        return {
          relationshipId: existingRelationship?.id || '',
          display: primaryClient.displayName,
          toClientProfileId: primaryClient.id,
          type: existingRelationship?.type || initialRelationshipType || '',
        };
      }) || [];

  // Add relationships from initialRelationships that aren't primary clients
  // (e.g., when creating a grandparent, we need to link to the parent)
  if (initialRelationships && household?.clientProfiles) {
    const primaryClientIds = new Set(
      household.possiblePrimaryClients.map((pc) => pc.id)
    );

    Object.entries(initialRelationships).forEach(
      ([toClientProfileId, relationshipType]) => {
        // Skip if already in the relationships array (primary client)
        if (primaryClientIds.has(toClientProfileId)) {
          return;
        }

        // Find the client profile in the full list
        const targetClientProfile = household?.clientProfiles?.find(
          (cp) => cp.id === toClientProfileId
        );

        if (targetClientProfile) {
          relationships.push({
            relationshipId: '',
            display: targetClientProfile.displayName,
            toClientProfileId: toClientProfileId,
            type: relationshipType,
          });
        }
      }
    );
  }

  const formValues: ClientProfileFormShape = {
    id: clientProfile?.id ?? null,
    firstName: clientProfile?.firstName || '',
    middleName: clientProfile?.middleName || '',
    lastName: clientProfile?.lastName || '',
    suffix: clientProfile?.suffix || '',
    preferredName: clientProfile?.preferredName || '',
    displayNamePattern:
      clientProfile?.displayNamePattern ||
      ClientProfileDisplayNamePattern.FirstLastSuffix,
    email: clientProfile?.email || '',
    address: {
      id: clientProfile?.address?.id ?? null,
      cityName: clientProfile?.address?.cityName ?? '',
      stateCode: clientProfile?.address?.stateCode || '',
    },
    dateOfBirth: clientProfile?.dateOfBirth || null,
    dateOfDeath: clientProfile?.dateOfDeath || null,
    isGrantor: clientProfile?.isGrantor ?? false,
    isPrimary: clientProfile?.isPrimary ?? false,
    notes: clientProfile?.notes ?? '',
    isDeceased: clientProfile?.isDeceased ?? false,
    relationships,
  };
  return formValues;
}

export function mapFormToCreatePayload(
  formData: ClientProfileFormShape,
  householdId: ClientProfileSideDrawerContextShape['householdId'],
  isPrimary: ClientProfileSideDrawerContextShape['isPrimary'],
  createdFromSuggestionID?: string
):
  | { input: CreateClientProfileWithDetailsInput; errorMessage?: never }
  | { input?: never; errorMessage: string } {
  if (!householdId) {
    return { errorMessage: 'No household ID found' };
  }

  const addressInput = {
    cityName: formData.address.cityName || undefined,
    stateCode: formData.address.stateCode,
    householdID: householdId,
  };

  const createClientProfileInput: CreateClientProfileWithDetailsInput = {
    clientProfile: {
      address: addressInput.stateCode ? addressInput : undefined,
      householdID: householdId,
      email: formData.email,
      firstName: formData.firstName?.trim() || '',
      middleName: formData.middleName?.trim() || '',
      lastName: formData.lastName?.trim() || '',
      suffix: formData.suffix?.trim() || '',
      // Left undefined rather than '' when blank so the server stores null and
      // falls back to the legal name for display.
      preferredName: formData.preferredName?.trim() || undefined,
      displayNamePattern: formData.displayNamePattern,
      isGrantor: formData.isGrantor,
      isPrimary: formData.isPrimary ?? isPrimary,
      dateOfBirth: getTypeOrUndefined(formData.dateOfBirth),
      dateOfDeath: formData.isDeceased
        ? getTypeOrUndefined(formData.dateOfDeath)
        : undefined,
      onboardedIndividualSuggestionIDs: createdFromSuggestionID
        ? [createdFromSuggestionID]
        : undefined,
      notes: formData.notes,
      isDeceased: formData.isDeceased,
    },
    relationships: compact(
      formData.relationships.map<CreateNewClientProfileRelationshipInput | null>(
        (relationship) => {
          if (relationship.type === '') {
            return null;
          }
          return {
            toClientProfileID: relationship.toClientProfileId,
            type: relationship.type,
          };
        }
      )
    ),
  };

  return { input: createClientProfileInput };
}

export function mapFormToUpdatePayload(
  formData: ClientProfileFormShape,
  householdId: ClientProfileSideDrawerContextShape['householdId'],
  clientProfileId: ClientProfileSideDrawerContextShape['clientProfileId']
):
  | {
      variables: UpdateClientProfileModalMutationVariables;
      errorMessage?: never;
    }
  | { variables?: never; errorMessage: string } {
  if (!clientProfileId) {
    return { errorMessage: 'No client profile ID found' };
  }

  if (!householdId) {
    return { errorMessage: 'No household ID found' };
  }

  const clientRelationships = formData.relationships
    .filter((relationship) => relationship.relationshipId || relationship.type)
    .map<UpdateClientProfileRelationship>((relationship) => {
      if (relationship.relationshipId && !relationship.type) {
        return {
          fromClientProfileID: clientProfileId,
          toClientProfileID: relationship.toClientProfileId,
          id: relationship.relationshipId,
          clearRelationship: true,
        };
      }
      return {
        fromClientProfileID: clientProfileId,
        toClientProfileID: relationship.toClientProfileId,
        id: relationship.relationshipId,
        relationship: {
          type: relationship.type as ClientProfileRelationshipType,
        },
      };
    });

  const variables: UpdateClientProfileModalMutationVariables = {
    clientProfileId,
    clientProfileAddress: formData.address.stateCode
      ? {
          cityName: getTypeOrUndefined<string>(formData.address.cityName),
          stateCode: getTypeOrUndefined<string>(formData.address.stateCode),
          clearCityName: !formData.address.cityName,
        }
      : {},
    clientProfile: {
      clearAddress: !formData.address.stateCode,
      email: formData.email,
      firstName: formData.firstName?.trim() || '',
      lastName: formData.lastName?.trim() || '',
      displayNamePattern: formData.displayNamePattern,
      isGrantor: formData.isGrantor,
      isPrimary: formData.isPrimary,
      notes: formData.notes,
      isDeceased: formData.isDeceased,
      ...getValueOrClear('dateOfBirth', formData.dateOfBirth),
      ...getValueOrClear(
        'dateOfDeath',
        formData.isDeceased ? formData.dateOfDeath : null
      ),
      ...getValueOrClearTrimmed('suffix', formData.suffix),
      ...getValueOrClearTrimmed('middleName', formData.middleName),
      ...getValueOrClearTrimmed('preferredName', formData.preferredName),
    },
    hasAddress: !!formData.address.stateCode,
    hasRelationships: !!clientRelationships.length,
    householdId: householdId,
    clientRelationships,
  };
  return { variables };
}
